import { Box, Button, Typography } from '@mui/material';
import MyCarousel from 'components/common/Carousel/Carousel';
import { PictureCarousel } from 'components/common/PictureCarousel';
import {
  WHITELABEL_EVENT_KEY,
  staticContent
} from 'components/constants/Constants';
import { Link, useParams } from 'react-router-dom';

import { HomepageProductHighlight } from './Data';
import HighlightProductCard from './Sections/HighlightProductCard';
import { useCallback, useEffect, useState } from 'react';
import { SegmentTag } from 'types/global';
import { ProductType } from 'types/Products';
import {
  SearchResult,
  SearchSortField,
  SearchSortNestedField,
  SortDirection
} from 'types/Search';
import { Search } from 'api/services/SearchService';
import { tss } from 'tss-react/mui';
import {
  ContentPost,
  ContentPostData_Homepage,
  ContentPostStatus,
  UXBContentCategory
} from 'portals/types';
import useTableHelper from '../Content/ContentTable/useTableHelper';
import { CarrouselSlide } from './Sections/CarrouselSlide';

const Homepage = () => {
  const { classes } = useStyles();
  const { event: eventKeyFromUrl } = useParams();
  const eventKey = WHITELABEL_EVENT_KEY ?? eventKeyFromUrl;

  const [data, setData] = useState<ContentPost<ContentPostData_Homepage>[]>([]);
  const { loadData } = useTableHelper<any>(
    UXBContentCategory.HOMEPAGE,
    () => 'Homepage'
  );
  const fetchData = useCallback(() => {
    loadData(
      setData,
      () => {},
      () => {},
      {
        query: '',
        page: 0,
        size: 200,
        sort: [],
        columnFilters: []
      }
    );
  }, [loadData, setData]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const [highlightedProducts, setHighlightedProducts] =
    useState<HomepageProductHighlight[]>();

  const [highlightedConcepts, setHighlightedConcepts] =
    useState<HomepageProductHighlight[]>();

  useEffect(() => {
    const searchRequest = (target: ProductType) => {
      return {
        query: '',
        target: target,
        segment: SegmentTag.PERSONALCARE,
        size: 12,
        from: 0,
        filters: [],
        sort: [
          {
            field: SearchSortField.EVENT_RANK,
            direction: SortDirection.ASCENDENT,
            nested_field: SearchSortNestedField.EVENT
          },
          {
            field: SearchSortField.EVENT_DISPLAY_NAME,
            direction: SortDirection.DESCENDENT,
            nested_field: SearchSortNestedField.EVENT
          }
        ],
        eventKey: eventKey
      };
    };
    const SearchResponseToHighlightedProductType = (
      searchResult: SearchResult[],
      target: ProductType
    ) => {
      const modifiedProducts: HomepageProductHighlight[] = searchResult
        //TODO: The hundred is a temporary fix for knowing wether the product is promoted pr not, ideally we should have a boolean here
        .filter(el => el.document.event.rank !== 100)
        .map(product => ({
          button: {
            link: `/${
              target === ProductType.INGREDIENTS ? 'ingredients' : 'concepts'
            }/permalink/${product.document.old_key}`,
            title:
              product?.document?.event?.product_private_attributes?.data
                ?.inStock?.[0] === 'false'
                ? 'Out of stock'
                : 'Order samples'
          },
          img: `${staticContent}/${
            product?.document?.event?.images?.[0]?.reference
              ? `events/${eventKey}/${product.document.event.images[0].reference}`
              : 'uxb/backgrounds/DEFAULT_IMAGE.webp'
          } `,
          tag: product?.document?.event?.product_private_attributes?.data
            ?.tagline?.[0],
          title: product.document.name
        }));
      target === ProductType.INGREDIENTS
        ? setHighlightedProducts(modifiedProducts)
        : setHighlightedConcepts(modifiedProducts);
    };
    Search(
      searchRequest(ProductType.INGREDIENTS),
      null,
      (res: any) =>
        SearchResponseToHighlightedProductType(
          res.data.results,
          ProductType.INGREDIENTS
        ),
      () => {}
    );
    Search(
      searchRequest(ProductType.FORMULATIONS),
      null,
      (res: any) =>
        SearchResponseToHighlightedProductType(
          res.data.results,
          ProductType.FORMULATIONS
        ),
      () => {}
    );
  }, [eventKey]);

  const responsive = (itemsLength: number) => {
    return {
      superLargeDesktop: {
        breakpoint: { max: 4000, min: 3000 },
        items: 3
      },
      desktop: {
        breakpoint: { max: 3000, min: 1350 },
        items: itemsLength > 2 ? 3 : 2,

        partialVisibilityGutter: -40
      },
      tablet: {
        breakpoint: { max: 1350, min: 920 },
        items: 2,
        partialVisibilityGutter: -40
      },
      mobile: {
        breakpoint: { max: 920, min: 0 },
        items: 1
      }
    };
  };

  return (
    <Box className={classes.wrapper}>
      <Box className={classes.headerImg}>
        <Box className={classes.headerImg}>
          <PictureCarousel arrowsPosition="Middle" shouldCoverBg autoTransition>
            {data
              .sort(
                (a, b) =>
                  new Date(b.created_at).getTime() -
                  new Date(a.created_at).getTime()
              )
              .filter(s => s.status === ContentPostStatus.PUBLISHED)
              .map(section => (
                <CarrouselSlide
                  key={section.key}
                  image={section.image}
                  title={section.data.title}
                  description={section.data.description}
                  productLink={section.data.productLink}
                />
              ))}
          </PictureCarousel>
        </Box>
      </Box>
      <Box
        sx={{
          backgroundImage: `url(${staticContent}/uxb/backgrounds/9dbabe8746054364308ea64f3895c561.jpg)`,
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
          paddingTop: '300px',
          paddingBottom: '100px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}
      >
        <Typography
          variant="h1"
          sx={{ textAlign: 'center', marginBottom: '80px' }}
        >
          Discover our innovative ingredients
        </Typography>
        {highlightedProducts && highlightedProducts.length > 0 && (
          <>
            <Typography
              variant="h2"
              sx={{
                textAlign: 'center',
                marginTop: '80px',
                marginBottom: '16px',
                textTransform: 'uppercase'
              }}
            >
              Highlighted products
            </Typography>
            <div className={classes.wrapperCards}>
              <MyCarousel
                responsive={responsive(highlightedProducts.length)}
                blackArrows
                itemClass={classes.itemClass}
                partialVisible={false}
              >
                {highlightedProducts.map(product => (
                  <HighlightProductCard key={product.title} data={product} />
                ))}
              </MyCarousel>
            </div>
          </>
        )}
        {highlightedConcepts && highlightedConcepts.length > 0 && (
          <>
            <Typography
              variant="h2"
              sx={{
                textAlign: 'center',
                marginTop: '80px',
                marginBottom: '16px',
                textTransform: 'uppercase'
              }}
            >
              Highlighted concepts
            </Typography>
            <div className={classes.wrapperCards}>
              <MyCarousel
                responsive={responsive(highlightedConcepts.length)}
                blackArrows
                partialVisible={false}
                itemClass={classes.itemClass}
              >
                {highlightedConcepts.map(product => (
                  <HighlightProductCard key={product.title} data={product} />
                ))}
              </MyCarousel>
            </div>
            <Button
              sx={{ mt: 9 }}
              variant="outlined"
              component={Link}
              to={'concepts/search'}
            >
              Discover them all
            </Button>
          </>
        )}
      </Box>
    </Box>
  );
};

export default Homepage;

const useStyles = tss.create(({ theme }) => ({
  wrapper: {
    position: 'relative',
    h2: {
      [theme.breakpoints.down('md')]: {
        fontSize: '24px'
      }
    }
  },
  headerImg: {
    width: '100%',
    height: 'calc(100vh - 64px)',
    [theme.breakpoints.down('sm')]: {
      height: 'calc(100vh - 56px)'
    }
  },
  wrapperCards: {
    maxWidth: '1110px',
    width: '80vw',
    margin: 'auto'
  },
  itemClass: {
    display: 'flex',
    paddingRight: theme.spacing(2),
    paddingLeft: theme.spacing(2)
  }
}));
